import { create } from 'zustand';
import { isTwoFactorChallenge, type AuthenticatedUser, type LoginResponse, type TwoFactorSetupDto } from '@ecom/contracts';
import { ApiError } from '@ecom/sdk';
import { api, errorMessage, refreshSession, withSession } from '@/lib/api-client';
import { clearSession, getRefreshToken, setAccessToken, setRefreshToken } from '@/lib/session';

/**
 * The session store (API → Store → Render).
 *
 * Components read this; only this file calls the auth API. `restore()` runs once
 * at startup: a page refresh should not sign a staff member out mid-shift, and
 * the persisted refresh token is what makes that possible.
 */

interface AuthState {
  user: AuthenticatedUser | null;
  /** True until the initial restore finishes — the shell waits on it. */
  loading: boolean;
  error: string | null;
  submitting: boolean;

  /**
   * The second step (ADR-116): the password was right and a code is due —
   * `verify` one from the app, or `setup` the app first where the store
   * requires it. No session until it is answered.
   */
  challenge: { token: string; step: 'verify' | 'setup' } | null;
  /** The secret to scan, during a setup at sign-in. */
  setup: TwoFactorSetupDto | null;
  /** Shown once after a setup at sign-in; the session waits until they are kept. */
  recoveryCodes: string[] | null;

  restore: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<boolean>;
  verifyCode: (code: string) => Promise<boolean>;
  startSetup: () => Promise<void>;
  finishSetup: (code: string) => Promise<void>;
  /** The recovery codes are kept: into the admin. */
  continueAfterCodes: () => void;
  cancelChallenge: () => void;
  signOut: () => Promise<void>;
  clearError: () => void;
}

/** A session from setup at sign-in, held until the recovery codes are acknowledged. */
let pending: LoginResponse | null = null;

/** Staff only: a customer account can authenticate but has no business here. */
function admit(session: LoginResponse, set: (state: Partial<AuthState>) => void): boolean {
  setAccessToken(session.accessToken);
  setRefreshToken(session.refreshToken);
  // The API would refuse every admin call, so say so at the door instead.
  if (session.user.role === 'CUSTOMER') {
    clearSession();
    set({ submitting: false, error: 'This account does not have access to the admin.' });
    return false;
  }
  set({ user: session.user, submitting: false, challenge: null, setup: null });
  return true;
}

/** A challenge past its five minutes goes back to the password. */
function failed(error: unknown, set: (state: Partial<AuthState>) => void, fallback: string): void {
  const expired = error instanceof ApiError && error.code === 'TWO_FACTOR_CHALLENGE_EXPIRED';
  set({ submitting: false, error: errorMessage(error, fallback), ...(expired ? { challenge: null, setup: null } : {}) });
}

/**
 * The restore in flight, shared. React's development mode runs the startup
 * effect twice, and two refreshes of one token read to the API as a stolen
 * token (TOKEN_REUSED): it revokes the whole session, and every reload signed
 * the operator out.
 */
let restoring: Promise<void> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  error: null,
  submitting: false,
  challenge: null,
  setup: null,
  recoveryCodes: null,

  restore() {
    restoring ??= (async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        set({ loading: false, user: null });
        return;
      }

      try {
        // The access token lives in memory only, so a reload always starts by
        // trading the stored refresh token for a fresh one — through the one
        // refresh path, which waits out another tab renewing the same session.
        if (!(await refreshSession())) {
          set({ user: null, loading: false });
          return;
        }

        const user = await api.auth.me();
        set({ user, loading: false });
      } catch {
        clearSession();
        set({ user: null, loading: false });
      }
    })().finally(() => {
      restoring = null;
    });
    return restoring;
  },

  async signIn(email, password) {
    set({ submitting: true, error: null });

    try {
      const result = await api.auth.login({ email, password });
      if (isTwoFactorChallenge(result)) {
        set({ submitting: false, challenge: { token: result.twoFactor.challenge, step: result.twoFactor.step } });
        return false;
      }
      return admit(result, set);
    } catch (error) {
      set({
        submitting: false,
        error: errorMessage(error, 'Could not sign in. Please try again.'),
      });
      return false;
    }
  },

  async verifyCode(code) {
    const challenge = get().challenge;
    if (!challenge) return false;
    set({ submitting: true, error: null });
    try {
      return admit(await api.auth.verifyTwoFactor({ challenge: challenge.token, code }), set);
    } catch (error) {
      failed(error, set, 'Could not check the code. Please try again.');
      return false;
    }
  },

  async startSetup() {
    const challenge = get().challenge;
    if (!challenge) return;
    set({ submitting: true, error: null });
    try {
      set({ setup: await api.auth.setUpTwoFactor(challenge.token), submitting: false });
    } catch (error) {
      failed(error, set, 'Could not start the setup. Please try again.');
    }
  },

  async finishSetup(code) {
    const challenge = get().challenge;
    if (!challenge) return;
    set({ submitting: true, error: null });
    try {
      const { recoveryCodes, ...session } = await api.auth.enableTwoFactor({ challenge: challenge.token, code });
      pending = session;
      set({ submitting: false, recoveryCodes, setup: null });
    } catch (error) {
      failed(error, set, 'Could not check the code. Please try again.');
    }
  },

  continueAfterCodes() {
    const session = pending;
    pending = null;
    set({ recoveryCodes: null });
    if (session) admit(session, set);
  },

  cancelChallenge() {
    pending = null;
    set({ challenge: null, setup: null, recoveryCodes: null, error: null });
  },

  async signOut() {
    const refreshToken = getRefreshToken();

    if (refreshToken) {
      try {
        // Revoking server-side matters: clearing local storage alone would
        // leave a working refresh token in any copy someone had taken.
        await withSession(() => api.auth.logout(refreshToken));
      } catch (error) {
        console.warn('[admin] sign-out call failed; clearing locally anyway', error);
      }
    }

    clearSession();
    set({ user: null });
  },

  clearError() {
    set({ error: null });
  },
}));
